import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { ShieldCheck, AlertCircle, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import TotalKlickLogo from "@/components/TotalKlickLogo";

const DEMO_USERS = [
  { role: "SuperAdmin", email: "andrea@totalklick.it", password: "SuperAdmin2026!" },
  { role: "Casa Madre Arredo", email: "casamadre.arredo@totalklick.it", password: "CasaMadre2026!" },
  { role: "Rivenditore Arredo", email: "rivenditore.arredo@totalklick.it", password: "Rivenditore2026!" },
  { role: "Casa Madre Moda", email: "casamadre.moda@totalklick.it", password: "CasaMadre2026!" },
  { role: "Rivenditore Moda", email: "rivenditore.moda@totalklick.it", password: "Rivenditore2026!" },
  { role: "Casa Madre Ciemme", email: "casamadre.ciemme@totalklick.it", password: "CasaMadre2026!" },
  { role: "Rivenditore Ciemme", email: "rivenditore.ciemme@totalklick.it", password: "Rivenditore2026!" },
];

export default function LoginPage() {
  const { login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.success) {
      setError(res.error);
      toast.error(res.error);
      return;
    }
    toast.success(`Benvenuto ${res.data.name || res.data.email}`);
    const role = res.data.role;
    if (role === "superadmin") navigate("/superadmin");
    else if (role === "casa_madre") navigate("/casa-madre");
    else if (role === "rivenditore") navigate("/rivenditore");
    else if (role === "customer") navigate("/account");
    else navigate("/");
  };

  const handleSso = () => {
    const state = window.crypto?.randomUUID?.() || String(Math.random());
    sessionStorage.setItem("tk_sso_state", state);
    const callbackUrl = `${window.location.origin}/auth/sso/callback`;
    const backend = process.env.REACT_APP_BACKEND_URL;
    const url =
      `${backend}/api/auth/sso/totalklick/start` +
      `?return_url=${encodeURIComponent(callbackUrl)}` +
      `&state=${encodeURIComponent(state)}`;
    window.location.href = url;
  };

  const fillDemo = (u) => {
    setEmail(u.email);
    setPassword(u.password);
  };

  return (
    <div className="min-h-screen flex bg-white" data-testid="login-page">
      {/* Left side: image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden border-r border-border">
        <img
          src="https://images.pexels.com/photos/5218431/pexels-photo-5218431.jpeg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-brand/40 via-black/20 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <Link to="/" className="flex items-center gap-2.5 self-start" data-testid="login-back-home">
            <TotalKlickLogo size={36} variant="mono" />
            <div className="leading-tight">
              <div className="font-heading font-bold">TotalKlick</div>
              <div className="overline text-white/70">Hub</div>
            </div>
          </Link>
          <div>
            <div className="overline text-white/70 mb-3">Multi-Network Distribution Hub</div>
            <h1 className="editorial-h1 text-5xl lg:text-6xl text-white mb-4">Una piattaforma per tutte le tue reti.</h1>
            <p className="text-white/85 max-w-md">Accedi con il tuo account TotalKlick per gestire le reti del Hub. Single Sign-On centralizzato e sicuro.</p>
          </div>
        </div>
      </div>

      {/* Right side: form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 relative">
        <div className="absolute top-6 right-6 flex items-center gap-3">
          <LanguageSwitcher />
          <Link to="/" data-testid="login-home-link" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
        </div>
        <div className="w-full max-w-md">
          <h2 className="font-heading font-bold text-3xl lg:text-4xl mb-2 tracking-tight" data-testid="login-title">
            {t("login_title")}
          </h2>
          <p className="text-sm text-muted-foreground mb-8">PRD v3.0 · TotalKlick SRLS · Andrea Sorrentino</p>

          <button
            data-testid="sso-totalklick-btn"
            onClick={handleSso}
            className="w-full h-14 rounded-sm bg-[#0A0A0A] hover:bg-[#1f1f1f] text-white text-base font-medium flex items-center justify-center gap-3 transition-colors"
          >
            <ShieldCheck className="w-5 h-5" />
            {t("login_sso_btn")}
          </button>
          <p className="text-xs text-muted-foreground mt-2 text-center">{t("login_sso_desc")}</p>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground uppercase tracking-wider">{t("login_or")}</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="overline text-muted-foreground mb-1.5 block">{t("login_email")}</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-11 px-3 rounded-sm border border-border focus:border-brand focus:ring-1 focus:ring-brand outline-none text-sm"
                placeholder="andrea@totalklick.it"
                required
              />
            </div>
            <div>
              <label className="overline text-muted-foreground mb-1.5 block">{t("login_password")}</label>
              <div className="relative">
                <input
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-11 px-3 pr-10 rounded-sm border border-border focus:border-brand focus:ring-1 focus:ring-brand outline-none text-sm"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  data-testid="toggle-password-visibility"
                  aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {error && (
              <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm p-3">
                <AlertCircle className="w-4 h-4" /> {error}
              </div>
            )}
            <button
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full h-11 rounded-sm bg-brand hover:bg-brand-hover text-white text-sm font-medium transition-colors disabled:opacity-50"
            >
              {loading ? "..." : t("login_submit")}
            </button>
          </form>

          <div className="mt-8 p-4 bg-accent/40 border border-border rounded-sm">
            <div className="overline text-brand mb-2.5">{t("login_demo_title")}</div>
            <div className="space-y-1">
              {DEMO_USERS.map((u, i) => (
                <button
                  key={i}
                  data-testid={`demo-user-${i}`}
                  type="button"
                  onClick={() => fillDemo(u)}
                  className="w-full text-left text-xs font-mono px-2.5 py-1.5 rounded-sm hover:bg-white transition-colors flex items-center justify-between"
                >
                  <span className="text-muted-foreground">{u.role}</span>
                  <span className="text-foreground">{u.email}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
